import { useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { gsap } from "@/lib/gsap";
import { FOOTER } from "@/constants/testIds";
import { useT } from "@/i18n";
import useReveal from "@/hooks/useReveal";
import { SERVICES_LIST } from "@/data/services";

export default function Footer() {
  const ref = useRef(null);
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();

  const NAV = [
    { id: "hero", label: t("nav.beranda") },
    { id: "values", label: t("nav.about") },
    { id: "mission", label: t("nav.reach") },
    { id: "services", label: t("nav.services") },
    { id: "products", label: t("nav.products") },
    { id: "works", label: t("nav.works") },
    { id: "insights", label: t("nav.insights") },
    { id: "contact", label: t("nav.contact") },
  ];

  useReveal(ref, () => {
    const ctx = gsap.context(() => {
      gsap.from("[data-anim='f-wordmark']", { opacity: 0, y: 90, duration: 1.3, ease: "power3.out" });
      gsap.from("[data-anim='f-col']", { opacity: 0, y: 40, duration: 0.85, stagger: 0.13 });
    }, ref);
    return () => ctx.revert();
  });

  const go = (id) => {
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (!el) return;
    const scroller = document.querySelector(".snap-root");
    if (scroller) {
      scroller.scrollTo({ top: el.offsetTop, behavior: "smooth" });
    } else {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer
      ref={ref}
      data-testid={FOOTER.root}
      className="relative overflow-hidden pt-28 pb-10 px-8 md:px-24 lg:px-40 grain"
      style={{ background: "var(--ink-2)", color: "white" }}
    >
      <div className="relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16 border-b border-white/10">
          <div className="md:col-span-5" data-anim="f-col">
            <div className="flex items-center gap-2 mb-7">
              <span className="font-display text-3xl font-bold" style={{ letterSpacing: "-0.02em" }}>Makario</span>
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: "var(--violet)" }} />
            </div>
            <p className="text-white/60 body-text text-sm max-w-md">{t("footer.tagline")}</p>
            <div className="mt-8 flex items-center gap-3 flex-wrap">
              {["LinkedIn", "Instagram", "Dribbble", "Behance"].map((s) => (
                <a key={s} href="#" className="px-4 py-2 rounded-full border border-white/15 text-[0.62rem] font-mono uppercase tracking-[0.24em] hover:bg-white hover:text-black transition-all duration-300">
                  {s}
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3" data-anim="f-col">
            <div className="eyebrow text-white/40 mb-6">{t("footer.explore")}</div>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.id}>
                  <button onClick={() => go(n.id)} className="link-line text-white/80 hover:text-white text-sm">
                    {n.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4" data-anim="f-col">
            <div className="eyebrow text-white/40 mb-6">{t("footer.disciplines")}</div>
            <ul className="space-y-3">
              {SERVICES_LIST.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="link-line text-white/80 hover:text-white text-sm">
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div data-anim="f-wordmark" className="py-14 overflow-hidden">
          <div
            className="font-display select-none"
            style={{
              fontSize: "clamp(4rem, 18vw, 18rem)",
              fontWeight: 800,
              letterSpacing: "-0.04em",
              lineHeight: 0.9,
              background: "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.08) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            MAKARIO.
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between text-[0.62rem] font-mono uppercase tracking-[0.24em] text-white/45">
          <div>© {new Date().getFullYear()} Makario. {t("footer.rights")}</div>
          <div className="flex gap-6">
            <Link to="/privacy" className="link-line">{t("footer.privacy")}</Link>
            <Link to="/privacy" className="link-line">{t("footer.terms")}</Link>
          </div>
          <div>{t("footer.madeIn")}</div>
        </div>
      </div>
    </footer>
  );
}
